/**
 * Items recently opened on this computer (epic PI-SPEC-SW-E289, feature
 * PI-SPEC-SW-1475, UX-PM-25, Claude Design frame 1d).
 *
 * One item and the reading pane record every item they open (a click, Enter,
 * a submit or a link; never a J/K step); One item lists the newest ones
 * under "Recently opened" so the PM does not retype IDs (PM-12, PM-14). Like
 * the run queue and the backlog sort this is per computer, so localStorage is
 * the right scope.
 *
 * Each entry keeps the project, the item id, its title and the state it had
 * when it was last read here (`updateRecentItem` refreshes it in place).
 * Newest first, de-duplicated by project + id, at most
 * {@link RECENT_ITEMS_CAP} entries. Storage errors (private mode, quota, bad
 * JSON) are swallowed: a missing list only means typing the ID. Titles are
 * tracker text and are rendered as React text only.
 */
import { useMemo, useSyncExternalStore } from "react";
import type { GroomingStage } from "@ttc-spectree/toolbox-contract";
import { BRIEF_STATE_WORDS, STAGE_LABELS, briefStateOf, type BriefState } from "@/lib/grooming/plain-words";

/** localStorage key of the recently opened items. */
export const RECENT_ITEMS_STORAGE_KEY = "spectree.grooming.recentItems";

/** How many entries the store keeps across every project. */
export const RECENT_ITEMS_CAP = 30;

/** How many items One item lists. */
export const RECENT_ITEMS_SHOWN = 8;

/** The state a recent item shows: a brief state, "Needs information", or no brief yet. */
export type RecentItemState = BriefState | "needs-information" | "no-brief";

export interface RecentItem {
  project: string;
  itemId: string;
  title: string;
  /** The item's state when it was last opened; absent from an entry that did not know it. */
  state?: RecentItemState;
  /** When it was last opened (ISO 8601). */
  openedAt: string;
}

const STATES: ReadonlySet<string> = new Set<RecentItemState>([
  "not-posted",
  "posted",
  "held-back",
  "out-of-date",
  "needs-information",
  "no-brief",
]);

/** The words for a recent item's state; brief states and the stage come from plain-words.ts. */
export function recentItemStateWords(state: RecentItemState): string {
  if (state === "needs-information") return STAGE_LABELS["needs-information"];
  if (state === "no-brief") return "No brief yet";
  return BRIEF_STATE_WORDS[state];
}

/**
 * A recent item's state from a backlog row (or a row built from the item
 * read): the brief's state when there is a brief, else "Needs information"
 * for an item below the grooming bar, else no brief yet. Pure.
 */
export function recentItemState(row: {
  stage: GroomingStage;
  aiSuggestion: { commentStatus: string; stale: boolean } | null;
}): RecentItemState {
  if (row.aiSuggestion) return briefStateOf({ commentStatus: row.aiSuggestion.commentStatus, stale: row.aiSuggestion.stale });
  return row.stage === "needs-information" ? "needs-information" : "no-brief";
}

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function sameItem(a: { project: string; itemId: string }, b: { project: string; itemId: string }): boolean {
  return a.itemId === b.itemId && a.project.toLowerCase() === b.project.toLowerCase();
}

function isRecentItem(value: unknown): value is RecentItem {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.project === "string" &&
    typeof entry.itemId === "string" &&
    typeof entry.title === "string" &&
    typeof entry.openedAt === "string" &&
    (entry.state === undefined || (typeof entry.state === "string" && STATES.has(entry.state)))
  );
}

function parse(raw: string | null | undefined): RecentItem[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isRecentItem) : [];
  } catch {
    return [];
  }
}

function readRaw(): string | null {
  try {
    return storage()?.getItem(RECENT_ITEMS_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

const listeners = new Set<() => void>();

/**
 * Remember that an item was opened on this computer, now (or `openedAt`).
 * It moves to the front; blank ids or projects are ignored, a blank title
 * keeps the one already remembered.
 */
export function recordRecentItem(entry: {
  project: string;
  itemId: string;
  title: string;
  state?: RecentItemState;
  openedAt?: string;
}): void {
  const project = entry.project.trim();
  const itemId = entry.itemId.trim();
  if (project.length === 0 || itemId.length === 0) return;
  try {
    const existing = parse(readRaw());
    const previous = existing.find((item) => sameItem(item, { project, itemId }));
    const trimmed = entry.title.trim();
    const title = trimmed.length > 0 ? trimmed : (previous?.title ?? "");
    const state = entry.state ?? previous?.state;
    const next: RecentItem = {
      project,
      itemId,
      title,
      ...(state ? { state } : {}),
      openedAt: entry.openedAt ?? new Date().toISOString(),
    };
    const items = [next, ...existing.filter((item) => !sameItem(item, next))].slice(0, RECENT_ITEMS_CAP);
    storage()?.setItem(RECENT_ITEMS_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // An item we cannot remember only means typing its ID next time.
    return;
  }
  for (const listener of listeners) listener();
}

/**
 * Update the remembered title and state of an item already in the list, in
 * place: its place and `openedAt` stay, since a refreshed read (a refetch
 * after a run, a post or a state change) is not a new open. Does nothing for
 * an item that is not in the list, or when nothing changed.
 */
export function updateRecentItem(entry: { project: string; itemId: string; title?: string; state?: RecentItemState }): void {
  const project = entry.project.trim();
  const itemId = entry.itemId.trim();
  if (project.length === 0 || itemId.length === 0) return;
  try {
    const existing = parse(readRaw());
    const index = existing.findIndex((item) => sameItem(item, { project, itemId }));
    const previous = existing[index];
    if (!previous) return;
    const trimmed = entry.title?.trim() ?? "";
    const title = trimmed.length > 0 ? trimmed : previous.title;
    const state = entry.state ?? previous.state;
    if (title === previous.title && state === previous.state) return;
    existing[index] = { ...previous, title, ...(state ? { state } : {}) };
    storage()?.setItem(RECENT_ITEMS_STORAGE_KEY, JSON.stringify(existing));
  } catch {
    // A state we cannot store only means the list shows the one from the last open.
    return;
  }
  for (const listener of listeners) listener();
}

/** Pick a project's items (any project when `project` is undefined), newest first, at most `limit`. */
function pick(items: RecentItem[], project: string | undefined, limit: number): RecentItem[] {
  const wanted = project?.toLowerCase();
  return items.filter((item) => wanted === undefined || item.project.toLowerCase() === wanted).slice(0, limit);
}

/** The items recently opened on this computer for a project (or every project), newest first. */
export function recentItems(project?: string, limit: number = RECENT_ITEMS_SHOWN): RecentItem[] {
  return pick(parse(readRaw()), project, limit);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === RECENT_ITEMS_STORAGE_KEY) listener();
  };
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
}

/** {@link recentItems}, re-read whenever an item is recorded (here or in another window). */
export function useRecentItems(project?: string, limit: number = RECENT_ITEMS_SHOWN): RecentItem[] {
  const raw = useSyncExternalStore(subscribe, readRaw, () => null);
  return useMemo(() => pick(parse(raw), project, limit), [raw, project, limit]);
}
